// frontend/src/components/event/EventRegistrationForm.js
import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Monitor, MapPin, Loader2 } from "lucide-react";

const EventRegistrationForm = ({
  event,
  onRegister,
  loading = false,
}) => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [participationType, setParticipationType] = useState("online");

  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${event.slug}`);
      return;
    }

    onRegister(participationType);
  };

  // Check available spots
  const onlineSpotsAvailable =
    event.capacity?.spotsRemaining?.online === null ||
    event.capacity?.spotsRemaining?.online > 0;
  const inPersonSpotsAvailable =
    event.capacity?.spotsRemaining?.in_person === null ||
    event.capacity?.spotsRemaining?.in_person > 0;

  // For online-only events
  if (event.format === "online") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!onlineSpotsAvailable ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
            {t({ de: "Keine Plätze mehr verfügbar", en: "No spots available" })}
          </div>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {isInvited
              ? t({ de: "Einladung annehmen & Zugang erhalten", en: "Accept Invitation & Get Access" })
              : t({ de: "Jetzt anmelden", en: "Register Now" })}
          </button>
        )}
      </form>
    );
  }

  // For in-person only events
  if (event.format === "in_person") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!inPersonSpotsAvailable ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
            {t({ de: "Keine Plätze mehr verfügbar", en: "No spots available" })}
          </div>
        ) : (
          <>
            <input type="hidden" value="in_person" />
            <button
              type="submit"
              disabled={loading}
              onClick={() => setParticipationType("in_person")}
              className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {isInvited
                ? t({ de: "Einladung annehmen & Zugang erhalten", en: "Accept Invitation & Get Access" })
                : t({ de: "Jetzt anmelden", en: "Register Now" })}
            </button>
          </>
        )}
      </form>
    );
  }

  // For hybrid events - show both options
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-sm font-medium text-gray-700 mb-2">
        {t({ de: "Wie möchten Sie teilnehmen?", en: "How would you like to participate?" })}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Online Option */}
        <button
          type="button"
          onClick={() => setParticipationType("online")}
          disabled={!onlineSpotsAvailable}
          className={`p-4 rounded-lg border-2 transition-all ${
            participationType === "online"
              ? "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${!onlineSpotsAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Monitor
            className={`w-6 h-6 mx-auto mb-2 ${
              participationType === "online" ? "text-[#EE005F]" : "text-gray-500"
            }`}
          />
          <div className="font-medium text-sm">
            {t({ de: "Online", en: "Online" })}
          </div>
          {event.capacity?.spotsRemaining?.online !== null && (
            <div className="text-xs text-gray-500 mt-1">
              {event.capacity.spotsRemaining.online}{" "}
              {t({ de: "Plätze", en: "spots" })}
            </div>
          )}
        </button>

        {/* In-Person Option */}
        <button
          type="button"
          onClick={() => setParticipationType("in_person")}
          disabled={!inPersonSpotsAvailable}
          className={`p-4 rounded-lg border-2 transition-all ${
            participationType === "in_person"
              ? "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${!inPersonSpotsAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <MapPin
            className={`w-6 h-6 mx-auto mb-2 ${
              participationType === "in_person"
                ? "text-[#EE005F]"
                : "text-gray-500"
            }`}
          />
          <div className="font-medium text-sm">
            {t({ de: "Vor Ort", en: "In-Person" })}
          </div>
          {event.capacity?.spotsRemaining?.in_person !== null && (
            <div className="text-xs text-gray-500 mt-1">
              {event.capacity.spotsRemaining.in_person}{" "}
              {t({ de: "Plätze", en: "spots" })}
            </div>
          )}
        </button>
      </div>

      <button
        type="submit"
        disabled={
          loading ||
          (participationType === "online" && !onlineSpotsAvailable) ||
          (participationType === "in_person" && !inPersonSpotsAvailable)
        }
        className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
        {isInvited
          ? t({ de: "Einladung annehmen & Zugang erhalten", en: "Accept Invitation & Get Access" })
          : t({ de: "Jetzt anmelden", en: "Register Now" })}
      </button>
    </form>
  );
};

export default EventRegistrationForm;
